/**
 * Opt-in cookie consent banner, shared by the marketing site and the app SPA.
 *
 * Shows until the visitor clicks Accept or Reject. There is no "close"
 * affordance on purpose: under CIPA, dismissing or navigating away does NOT
 * count as consent, so the only two outcomes are an explicit Accept or
 * Reject. Until Accept is clicked, no non-necessary tracker loads.
 *
 * Styling is fully inline so the banner renders identically on both apps
 * (Tailwind on marketing, design-system tokens on web) and so the two
 * buttons stay strictly symmetrical — same size, font, weight, and color —
 * as regulators require.
 *
 * onAccept lets each app boot its own trackers in place (load the ad pixels,
 * init PostHog) without a page reload. Reject loads nothing.
 */

import { useEffect, useState } from 'react';

import { getCookieConsent, setCookieConsent } from './cookie-consent';

const COPY =
  'We use cookies to operate our services and share information about your ' +
  'use of our site with our advertising and analytics partners in the ' +
  'United States. By clicking “Accept,” you agree to our use of ' +
  'non-necessary cookies. By clicking “Reject,” we will only use ' +
  'necessary cookies.';

const BUTTON_STYLE: React.CSSProperties = {
  flex: '1 1 0',
  minWidth: 120,
  padding: '12px 20px',
  borderRadius: 10,
  border: '1px solid #f6f1e4',
  background: '#f6f1e4',
  color: '#2b2417',
  fontFamily: 'inherit',
  fontSize: 15,
  fontWeight: 600,
  lineHeight: 1.2,
  cursor: 'pointer',
};

export function CookieBanner({
  onAccept,
  compact = false,
}: {
  onAccept?: () => void;
  compact?: boolean;
}): React.ReactElement | null {
  // Starts hidden and decides after mount, rather than reading consent in the
  // initialiser. The marketing site is prerendered, and there is no
  // localStorage on the server: the initialiser would see "no consent" and
  // bake a visible banner into the HTML, which then hydrates to hidden for
  // every returning visitor who already accepted — a mismatch on each repeat
  // visit. The cost is that a first-time visitor sees the banner one frame
  // late; the alternative is React discarding the prerendered page.
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (getCookieConsent() === null) setVisible(true);
  }, []);

  if (!visible) return null;

  const accept = (): void => {
    setCookieConsent('accepted');
    setVisible(false);
    onAccept?.();
  };

  const reject = (): void => {
    setCookieConsent('rejected');
    setVisible(false);
  };

  return (
    <div
      role="dialog"
      aria-label="Cookie consent"
      style={{
        position: 'fixed',
        left: compact ? 'max(14px, env(safe-area-inset-left))' : 0,
        right: compact ? 'auto' : 0,
        bottom: compact ? 'max(14px, env(safe-area-inset-bottom))' : 0,
        width: compact ? 'min(520px, calc(100% - 28px))' : 'auto',
        zIndex: 2147483000,
        background: compact ? 'rgba(255,255,255,0.97)' : '#2b2417',
        color: compact ? '#313238' : '#f6f1e4',
        border: compact ? '1px solid #dedfe2' : '0',
        borderRadius: compact ? 20 : 0,
        boxShadow: compact ? '0 18px 55px rgba(0,0,0,0.14)' : '0 -2px 24px rgba(0,0,0,0.35)',
        backdropFilter: compact ? 'blur(18px)' : undefined,
        padding: compact
          ? 16
          : '20px max(16px, env(safe-area-inset-left)) calc(20px + env(safe-area-inset-bottom))',
        boxSizing: 'border-box',
      }}
    >
      <div
        style={{
          maxWidth: compact ? 'none' : 720,
          margin: '0 auto',
          display: 'flex',
          flexDirection: 'column',
          gap: compact ? 13 : 16,
        }}
      >
        <p
          style={{
            margin: 0,
            fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, sans-serif',
            fontSize: 14,
            lineHeight: 1.5,
          }}
        >
          {compact ? (
            <>
              We use necessary cookies to operate Ori. With your permission, we also use
              analytics and advertising cookies.{' '}
              <a href="/privacy" style={{ color: 'inherit', textUnderlineOffset: 2 }}>
                Privacy Policy
              </a>
            </>
          ) : COPY}
        </p>
        <div style={{ display: 'flex', gap: compact ? 8 : 12 }}>
          <button
            type="button"
            style={{
              ...BUTTON_STYLE,
              minWidth: compact ? 96 : BUTTON_STYLE.minWidth,
              padding: compact ? '10px 16px' : BUTTON_STYLE.padding,
              borderColor: compact ? '#dedfe2' : '#f6f1e4',
              background: compact ? '#f3f3f3' : BUTTON_STYLE.background,
              color: compact ? '#151515' : BUTTON_STYLE.color,
              fontSize: compact ? 13 : BUTTON_STYLE.fontSize,
            }}
            onClick={accept}
          >
            Accept
          </button>
          <button
            type="button"
            style={{
              ...BUTTON_STYLE,
              minWidth: compact ? 96 : BUTTON_STYLE.minWidth,
              padding: compact ? '10px 16px' : BUTTON_STYLE.padding,
              borderColor: compact ? '#dedfe2' : '#f6f1e4',
              background: compact ? '#f3f3f3' : BUTTON_STYLE.background,
              color: compact ? '#151515' : BUTTON_STYLE.color,
              fontSize: compact ? 13 : BUTTON_STYLE.fontSize,
            }}
            onClick={reject}
          >
            Reject
          </button>
        </div>
      </div>
    </div>
  );
}
